import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Users, UserX, X } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { getTeam, inviteTeamMember, cancelTeamInvite, removeTeamMember, setAutoAddNewEvents } from '@/lib/pro-team-api';

export default function ProTeamPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [rowBusy, setRowBusy] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErrorCode(null); setErrorMsg(null);
    try { setRoster(await getTeam()); }
    catch (e) { setErrorCode(e?.code || null); setErrorMsg(e?.message || 'Could not load team.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function onInvite() {
    setInviteError(null);
    if (!inviteEmail.trim()) { setInviteError('Enter an email address.'); return; }
    setInviteBusy(true);
    try {
      const res = await inviteTeamMember(inviteEmail.trim().toLowerCase());
      window.alert(res.already_pending
        ? `${inviteEmail} already has a pending invite.`
        : `Invite sent to ${inviteEmail}. They\u2019ll show up here once they accept.`);
      setInviteEmail('');
      await load();
    } catch (e) {
      const map = {
        SELF_INVITE: "You can\u2019t invite yourself.",
        PRO_INACTIVE: 'Renew your PRO subscription to invite teammates.',
        SEAT_LIMIT_REACHED: `All ${roster?.seats_max ?? 5} team seats are used.`,
        ALREADY_MEMBER: 'This person is already on your team.',
      };
      setInviteError(map[e?.code] || e?.message || 'Could not send invite.');
    } finally { setInviteBusy(false); }
  }

  async function onCancelInvite(inv) {
    setRowBusy(inv.id);
    try { await cancelTeamInvite(inv.id); await load(); }
    catch (e) { window.alert(e?.message || 'Could not cancel invite.'); }
    finally { setRowBusy(null); }
  }

  async function onToggleAutoAdd(m, next) {
    setRowBusy(m.member_user_id);
    try { await setAutoAddNewEvents(m.member_user_id, next); await load(); }
    catch (e) { window.alert(e?.message || 'Could not update.'); }
    finally { setRowBusy(null); }
  }

  async function onRemove(m) {
    setRowBusy(m.member_user_id);
    try {
      const res = await removeTeamMember(m.member_user_id);
      const n = res.revoked_events?.length ?? 0;
      window.alert(`Team member removed.${n > 0 ? ` Their co-host access on ${n} event(s) has been revoked.` : ''}`);
      await load();
    } catch (e) { window.alert(e?.message || 'Could not remove.'); }
    finally { setRowBusy(null); setConfirmRemove(null); }
  }

  const isPro = (user?.account_type || '').toUpperCase() === 'PROFESSIONAL';
  const seatMax = roster?.seats_max ?? 5;
  const seatUsed = roster?.seats_used ?? 0;
  const seatsLeft = Math.max(0, seatMax - seatUsed);
  const inviteBlocked = !roster?.pro_subscription_active || seatsLeft === 0;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="pro-team-screen">
      <div className="flex items-center justify-between mb-1">
        <button onClick={() => navigate(-1)} className="p-1 -ml-1 hover:bg-[#F3F5F8] rounded-lg transition-colors" data-testid="pro-team-back-btn"><ChevronLeft className="w-5 h-5 text-[#0B1B46]" /></button>
        <h1 className="text-xl font-extrabold text-[#0B1B46]" data-testid="pro-team-title">Team seats</h1>
        <div className="w-5" />
      </div>
      <p className="text-sm text-[#5B6472] mb-4">
        Invite up to {seatMax} teammates to help run your events. Each teammate can be granted
        co-host access to any of your events; contributor privacy is untouched.
      </p>

      {loading ? (
        <div className="flex justify-center py-10" data-testid="pro-team-loading"><div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
      ) : errorCode === 'PROFESSIONAL_ONLY' || (!isPro && errorMsg) ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-5" data-testid="pro-team-upsell">
          <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-1">Professional accounts only</p>
          <p className="text-sm text-[#5B6472] mb-3">Team seats are part of ALLVIEWS PRO. Switch account type in settings, then start a subscription.</p>
          <button onClick={() => navigate('/host-premium')} className="h-9 px-4 bg-[#146BFF] text-white font-semibold rounded-lg text-sm hover:bg-[#0F55CC] transition-colors" data-testid="pro-team-upsell-btn">See PRO plans</button>
        </div>
      ) : errorMsg && !roster ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-5" data-testid="pro-team-error">
          <p className="text-sm font-semibold text-[#DC2626]">{errorMsg}</p>
          <button onClick={load} className="mt-2 h-9 px-4 bg-[#146BFF] text-white font-semibold rounded-lg text-sm" data-testid="pro-team-retry-btn">Retry</button>
        </div>
      ) : roster ? (
        <div className="flex flex-col gap-4">
          {/* Lapse warning */}
          {!roster.pro_subscription_active && (
            <div className="bg-[#FFF6E5] border border-[#FFE4B3] rounded-xl p-4" data-testid="pro-team-lapsed-notice">
              <p className="text-[10px] font-extrabold text-[#B86500] tracking-widest uppercase mb-1">PRO paused</p>
              <p className="text-sm text-[#5B6472] mb-2">Your PRO subscription has lapsed. Existing team access on live events stays active — but you can&apos;t send new invites or grant new co-host access until you renew.</p>
              <button onClick={() => navigate('/host-premium')} className="h-9 px-4 bg-[#B86500] text-white font-semibold rounded-lg text-sm hover:bg-[#9A5500] transition-colors" data-testid="pro-team-renew-btn">Renew PRO</button>
            </div>
          )}

          {/* Seat meter */}
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="pro-team-seat-meter">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-[#0B1B46]" />
              <span className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase">Seats</span>
              <span className="flex-1" />
              <span className="text-base font-extrabold text-[#0B1B46]" data-testid="pro-team-seat-count">{seatUsed} / {seatMax}</span>
            </div>
            <div className="h-1.5 bg-[#F3F5F8] rounded-full overflow-hidden">
              <div className="h-full bg-[#146BFF] rounded-full transition-all" style={{ width: `${Math.min(100, (seatUsed / seatMax) * 100)}%` }} />
            </div>
            <p className="text-sm text-[#5B6472] mt-2" data-testid="pro-team-seats-left">{seatsLeft} seat{seatsLeft === 1 ? '' : 's'} available.</p>
          </div>

          {/* Invite form */}
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="pro-team-invite-card">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-2">Invite a teammate</p>
            <div className="flex gap-2">
              <input
                type="email" value={inviteEmail}
                onChange={(e) => { setInviteEmail(e.target.value); setInviteError(null); }}
                placeholder="name@company.com"
                disabled={inviteBlocked || inviteBusy}
                className="flex-1 h-10 px-3 border border-[#E3E8EF] rounded-lg text-sm text-[#0B1B46] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#146BFF] disabled:opacity-50"
                data-testid="pro-team-invite-email-input"
              />
              <button onClick={onInvite} disabled={inviteBlocked || inviteBusy}
                className="h-10 px-4 bg-[#146BFF] text-white font-semibold rounded-lg text-sm hover:bg-[#0F55CC] transition-colors disabled:opacity-50"
                data-testid="pro-team-invite-btn">{inviteBusy ? 'Sending\u2026' : 'Send invite'}</button>
            </div>
            {inviteError && <p className="text-xs font-semibold text-[#DC2626] mt-2" data-testid="pro-team-invite-error">{inviteError}</p>}
          </div>

          {/* Pending invites */}
          {roster.pending_invites?.length > 0 && (
            <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="pro-team-pending-card">
              <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-2">Pending invites</p>
              <div className="flex flex-col divide-y divide-[#E3E8EF]">
                {roster.pending_invites.map((inv) => (
                  <div key={inv.id} className="flex items-center gap-3 py-3" data-testid={`pro-team-pending-${inv.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#0B1B46] truncate">{inv.to_email}</p>
                      <p className="text-[11px] text-[#5B6472] mt-0.5">Sent {new Date(inv.created_at).toLocaleDateString()} &middot; Expires {new Date(inv.expires_at).toLocaleDateString()}</p>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FFF6E5] text-[#B86500]">Pending</span>
                    <button onClick={() => onCancelInvite(inv)} disabled={rowBusy === inv.id}
                      className="p-1.5 hover:bg-[#FEF2F2] rounded transition-colors disabled:opacity-50"
                      data-testid={`pro-team-pending-cancel-${inv.id}`} aria-label="Cancel invite">
                      {rowBusy === inv.id
                        ? <div className="w-4 h-4 border-2 border-[#DC2626] border-t-transparent rounded-full animate-spin" />
                        : <X className="w-4 h-4 text-[#DC2626]" />}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active members */}
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="pro-team-members-card">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-2">Team members</p>
            {roster.members?.length === 0 ? (
              <p className="text-sm text-[#5B6472]" data-testid="pro-team-members-empty">No one on your team yet. Invite someone above to get started.</p>
            ) : (
              <div className="flex flex-col divide-y divide-[#E3E8EF]">
                {roster.members.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 py-3" data-testid={`pro-team-member-${m.member_user_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#0B1B46] truncate">
                        {(m.member_first_name || m.member_last_name) ? `${m.member_first_name || ''} ${m.member_last_name || ''}`.trim() : (m.member_email || 'Team member')}
                      </p>
                      {m.member_email && <p className="text-[11px] text-[#5B6472] truncate mt-0.5">{m.member_email}</p>}
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[11px] text-[#5B6472]">Auto-add to new events</span>
                        <button onClick={() => onToggleAutoAdd(m, !m.auto_add_new_events)} disabled={rowBusy === m.member_user_id}
                          className={`w-9 h-5 rounded-full relative transition-colors ${m.auto_add_new_events ? 'bg-[#146BFF]' : 'bg-[#D1D5DB]'} disabled:opacity-50`}
                          role="switch" aria-checked={m.auto_add_new_events}
                          data-testid={`pro-team-member-autoadd-${m.member_user_id}`}>
                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${m.auto_add_new_events ? 'left-[18px]' : 'left-0.5'}`} />
                        </button>
                      </div>
                    </div>
                    <button onClick={() => setConfirmRemove(m)} disabled={rowBusy === m.member_user_id}
                      className="p-1.5 hover:bg-[#FEF2F2] rounded transition-colors disabled:opacity-50"
                      data-testid={`pro-team-member-remove-${m.member_user_id}`} aria-label="Remove member">
                      {rowBusy === m.member_user_id
                        ? <div className="w-4 h-4 border-2 border-[#DC2626] border-t-transparent rounded-full animate-spin" />
                        : <UserX className="w-4 h-4 text-[#DC2626]" />}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Remove confirmation modal */}
      {confirmRemove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1B46]/55 p-4" onClick={() => setConfirmRemove(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="pro-team-remove-modal">
            <h3 className="text-lg font-extrabold text-[#0B1B46] mb-2">Remove teammate?</h3>
            <p className="text-sm text-[#5B6472] mb-4">
              This will immediately revoke {confirmRemove.member_email || 'this person'}&apos;s co-host access on all of your events. You can invite them again later.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmRemove(null)}
                className="flex-1 h-10 border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8] transition-colors"
                data-testid="pro-team-remove-cancel-btn">Cancel</button>
              <button onClick={() => onRemove(confirmRemove)}
                className="flex-1 h-10 bg-[#DC2626] text-white font-semibold rounded-lg hover:bg-[#B91C1C] transition-colors"
                data-testid="pro-team-remove-confirm-btn">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
